/**
 * Field Section Component
 * Groups fields into collapsible sections with configurable layouts
 */

import React, { useState, useEffect, useRef } from 'react';
import { FieldSchema, ValidationResult, getConfigValue } from '@/config/fieldConfig';
import { SectionConfig } from '@/config/fieldSections';
import { useI18n } from '@/i18n';
import FieldRenderer from './FieldRenderer';

export interface FieldSectionProps {
  sectionKey: string;
  sectionConfig: SectionConfig;
  fields: Array<{ name: string; schema: FieldSchema }>;
  values: Record<string, any>;
  onChange: (name: string, value: any) => void;
  errors?: Record<string, ValidationResult>;
  disabled?: boolean;
  appContext?: any;
  onBrowseTools?: (fieldName: string) => void;
  collapsible?: boolean;
  defaultCollapsed?: boolean;
  /** Whether this section should be highlighted (flash animation) */
  highlight?: boolean;
  /** Optional description rendered as an info banner between header and fields */
  description?: React.ReactNode;
}

export const FieldSection: React.FC<FieldSectionProps> = ({
  sectionKey,
  sectionConfig,
  fields,
  values,
  onChange,
  errors = {},
  disabled = false,
  appContext,
  onBrowseTools,
  collapsible = true,
  defaultCollapsed = false,
  highlight = false,
  description,
}) => {
  const { getSectionTitle, getSectionDescription } = useI18n();
  const [isCollapsed, setIsCollapsed] = useState(defaultCollapsed);
  const sectionRef = useRef<HTMLDivElement>(null);

  // Scroll into view and expand when highlighted
  useEffect(() => {
    if (highlight && sectionRef.current) {
      // Expand if collapsed
      if (isCollapsed) {
        setIsCollapsed(false);
      }
      // Scroll into view with smooth animation
      setTimeout(() => {
        sectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 100);
    }
  }, [highlight, isCollapsed]);

  // Filter out fields that should be hidden based on showIf
  const visibleFields = fields.filter((field) => {
    if (!field.schema.showIf) return true;
    return field.schema.showIf(values);
  });

  // Don't render section if no visible fields
  if (visibleFields.length === 0) {
    return null;
  }

  // Determine grid columns based on section config
  const gridClass = sectionConfig.columns === 2
    ? 'grid grid-cols-1 md:grid-cols-2 gap-4'
    : 'flex flex-col gap-4';

  return (
    <div
      ref={sectionRef}
      className={`
        bg-white rounded-lg border shadow-sm transition-all duration-300
        ${highlight ? 'border-blue-400 ring-2 ring-blue-200 animate-pulse' : 'border-gray-200'}
      `}
    >
      {/* Section Header */}
      <div
        className={`
          px-4 py-3 border-b border-gray-200 flex items-center justify-between
          ${collapsible ? 'cursor-pointer hover:bg-gray-50' : ''}
          ${highlight ? 'bg-blue-50' : ''}
        `}
        onClick={() => collapsible && setIsCollapsed(!isCollapsed)}
      >
        <h3 className={`text-lg font-semibold ${highlight ? 'text-blue-700' : 'text-gray-800'}`}>
          {getSectionTitle(sectionKey) || sectionConfig.title}
        </h3>

        {collapsible && (
          <button
            type="button"
            className="text-gray-500 hover:text-gray-700 transition-transform"
            style={{ transform: isCollapsed ? 'rotate(-90deg)' : 'rotate(0deg)' }}
          >
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 9l-7 7-7-7"
              />
            </svg>
          </button>
        )}
      </div>

      {/* Section Content */}
      {!isCollapsed && (
        <div className="p-4">
          {(description || getSectionDescription(sectionKey)) && (
            <div className="mb-4 px-3 py-2.5 bg-gray-50 border border-gray-200 rounded-md text-sm text-gray-600 leading-relaxed">
              {description || getSectionDescription(sectionKey)}
            </div>
          )}
          <div className={gridClass}>
            {visibleFields.map((field) => (
              <div
                key={field.name}
                className={field.schema.fullWidth && sectionConfig.columns === 2 ? 'md:col-span-2' : ''}
              >
                <FieldRenderer
                  name={field.name}
                  value={getConfigValue(values, field.name)}
                  onChange={(value) => onChange(field.name, value)}
                  schema={field.schema}
                  error={errors[field.name]}
                  disabled={disabled}
                  appContext={appContext || values}
                  onBrowseTools={
                    field.schema.hasToolBrowser && onBrowseTools
                      ? () => onBrowseTools(field.name)
                      : undefined
                  }
                  formData={values}
                  onFormDataChange={onChange}
                />
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default FieldSection;
